import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import './AdminLeaderboardArchive.css';

const AdminLeaderboardArchive = () => {
  const navigate = useNavigate();
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [archiveName, setArchiveName] = useState('');

  useEffect(() => {
    loadArchives();
  }, []);

  const loadArchives = async () => {
    try {
      const q = query(collection(db, 'leaderboardArchives'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const archiveList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setArchives(archiveList);
    } catch (error) {
      console.error('Error loading archives:', error);
      toast.error('Failed to load archives');
    }
  };

  const createArchive = async () => {
    if (!archiveName.trim()) {
      toast.error('Please enter an archive name');
      return;
    }

    setLoading(true);
    try {
      // Fetch current leaderboard
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by YP (descending), then by matches (descending)
      const sortedUsers = users.sort((a, b) => {
        const aYP = a.YP || 0;
        const bYP = b.YP || 0;
        
        if (aYP !== bYP) {
          return bYP - aYP;
        }
        
        const aMatches = a.stats?.matches || 0;
        const bMatches = b.stats?.matches || 0;
        return bMatches - aMatches;
      });

      // Calculate ranks and divisions
      const leaderboardData = sortedUsers.map((user, index) => {
        const rank = index + 1;
        let division = 'Red Slifer';
        
        if (rank >= 1 && rank <= 4) {
          division = 'Blue Obelisk';
        } else if (rank >= 5 && rank <= 12) {
          division = 'Yellow Ra';
        }

        return {
          userId: user.id,
          pseudo: user.pseudo,
          profilePicture: user.profilePicture || '/default-avatar.png',
          rank: rank,
          division: division,
          YP: user.YP || 0,
          stats: {
            matches: user.stats?.matches || 0,
            wins: user.stats?.wins || 0,
            losses: user.stats?.losses || 0
          }
        };
      });

      // Create archive document
      await addDoc(collection(db, 'leaderboardArchives'), {
        name: archiveName.trim(),
        createdAt: serverTimestamp(),
        totalDuelists: leaderboardData.length,
        leaderboard: leaderboardData
      });

      toast.success(`✅ Archive "${archiveName}" created successfully!`);
      setArchiveName('');
      loadArchives();
    } catch (error) {
      console.error('Error creating archive:', error);
      toast.error('Failed to create archive');
    } finally {
      setLoading(false);
    }
  };

  const deleteArchive = async (archiveId, archiveName) => {
    if (!window.confirm(`Are you sure you want to delete archive "${archiveName}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'leaderboardArchives', archiveId));
      toast.success(`✅ Archive "${archiveName}" deleted`);
      loadArchives();
    } catch (error) {
      console.error('Error deleting archive:', error);
      toast.error('Failed to delete archive');
    }
  };

  const viewArchive = (archiveId) => {
    navigate(`/leaderboard-archive/${archiveId}`);
  };

  return (
    <div className="admin-leaderboard-archive">
      <div className="archive-header">
        <h1>📊 Leaderboard Archive Manager</h1>
        <button className="back-button" onClick={() => navigate('/admin-panelx-952504')}>
          ← Back to Admin
        </button>
      </div>

      <div className="create-archive-section">
        <h2>Create New Archive</h2>
        <div className="create-archive-form">
          <input
            type="text"
            value={archiveName}
            onChange={(e) => setArchiveName(e.target.value)}
            placeholder="Enter archive name (e.g., January 2026)"
            className="archive-name-input"
          />
          <button 
            onClick={createArchive} 
            disabled={loading}
            className="create-archive-btn"
          >
            {loading ? 'Creating...' : '📸 Create Archive'}
          </button>
        </div>
        <p className="archive-description">
          Creates a snapshot of the current leaderboard with all duelist rankings
        </p>
      </div>

      <div className="archives-list-section">
        <h2>Existing Archives ({archives.length})</h2>
        {archives.length === 0 ? (
          <div className="empty-state">
            <p>No archives yet. Create your first archive!</p>
          </div>
        ) : (
          <div className="archives-list">
            {archives.map((archive) => (
              <div key={archive.id} className="archive-item">
                <div className="archive-info">
                  <h3>{archive.name}</h3>
                  <div className="archive-details">
                    <span className="archive-date">
                      {archive.createdAt?.toDate?.()?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) || 'Unknown date'}
                    </span>
                    <span className="archive-count">
                      👥 {archive.totalDuelists} duelists
                    </span>
                  </div>
                </div>
                <div className="archive-actions">
                  <button 
                    onClick={() => viewArchive(archive.id)}
                    className="view-archive-btn"
                  >
                    👁️ View
                  </button>
                  <button 
                    onClick={() => deleteArchive(archive.id, archive.name)}
                    className="delete-archive-btn"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLeaderboardArchive;
